import type { Editor } from '@tiptap/core';
import classNames from 'classnames';
import lodash from 'lodash';
import React from 'react';

import type { MediaUploadError } from './../../ricos-content';
import type { VideoData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { ComponentData, LegacyEditorPluginConfig, TFunction } from './../../ricos-types';
import type { ReactPlayer } from './../../wix-react-player';
import { Loader, MediaItemErrorMsg } from './../../ui-components';

import { getContainerClasses } from '../../_shared/figure/get-container-classes';
import { VIDEO_ROOT } from '../data-hooks';
import styles from '../statics/styles/default-video-styles-scss';
import { Video } from '../viewer/video';

type Props = {
  componentData: VideoData & { error?: MediaUploadError };
  isLoading: boolean;
  loadingPercentage?: number;
  updateAttributes: (componentData: ComponentData) => void;
  config: LegacyEditorPluginConfig;
  nodeId: string;
  t: TFunction;
  editor: Editor;
};

interface State {
  isLoaded: boolean;
  iosFixKey: number;
  shouldRenderDurationFix?: boolean;
}

export class VideoComponent extends React.Component<Props, State> {
  private containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  private durationFixTimeoutId: ReturnType<typeof setTimeout> | undefined;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoaded: false,
      iosFixKey: 1,
    };
  }

  componentDidMount() {
    this.handlePlayerFocus();
  }

  componentDidUpdate() {
    this.handlePlayerFocus();
  }

  componentWillUnmount(): void {
    clearTimeout(this.durationFixTimeoutId);
  }

  handlePlayerFocus() {
    const element = this.containerRef.current?.querySelector('iframe, video');

    if (element) {
      (element as HTMLElement).tabIndex = -1;
    }
  }

  handleReady = (player: React.MutableRefObject<ReactPlayer | null>) => {
    if (!this.state.isLoaded) {
      this.setState({ isLoaded: true });
    }
    this.handleDuration(player);
  };

  handleDuration = (player: React.MutableRefObject<ReactPlayer | null>) => {
    setTimeout(() => {
      const duration = player.current?.getDuration();
      if (duration) {
        this.saveDurationToData(duration);
      } else {
        this.setState({ shouldRenderDurationFix: true });
      }
    }, 3000);
  };

  renderLoader = () => {
    const isCustomVideo =
      'video' in this.props.componentData && !!this.props.componentData.video?.src?.id;
    return (
      <div className={styles.videoOverlay}>
        <Loader
          type={'medium'}
          isVerySlowFakeLoader={isCustomVideo}
          percent={this.props.loadingPercentage}
          disableProgress={!this.props.isLoading && !this.state.isLoaded}
        />
      </div>
    );
  };

  saveDurationToData = (duration: number) => {
    const { componentData, updateAttributes } = this.props;
    const video = 'video' in componentData ? componentData.video : {};
    updateAttributes({ ...componentData, video: { ...(video || {}), duration } });
  };

  renderPlayer = () => {
    const { componentData, config, nodeId } = this.props;
    return (
      <Video
        componentData={componentData}
        config={config}
        onReady={this.handleReady}
        isLoaded={this.state.isLoaded}
        key={this.state.iosFixKey}
        nodeId={nodeId}
        isLight={false}
        disabled
      />
    );
  };

  renderPlayerDurationFix = () => {
    const { componentData, config, nodeId } = this.props;

    const play = (player: React.MutableRefObject<ReactPlayer | null>): boolean => {
      const internalPlayer = player.current?.getInternalPlayer();
      if (internalPlayer && 'play' in internalPlayer && lodash.isFunction(internalPlayer.play)) {
        internalPlayer.play();
        return true;
      }

      return false;
    };

    // In some instances the video doesn't completely load, and we don't get its duration. This is a fix for that. (happens in facebook)
    const onReady = (player: React.MutableRefObject<ReactPlayer | null>) => {
      const hasPlayed = play(player);
      if (hasPlayed) {
        return;
      }
      // try again, sometimes it takes a while
      this.durationFixTimeoutId = setTimeout(() => {
        play(player);
      }, 10000);
    };

    return (
      <div className={styles.hide_duration}>
        <Video
          componentData={componentData}
          config={config}
          onReady={onReady}
          isLight={false}
          onDuration={duration => {
            this.setState({ shouldRenderDurationFix: false });
            this.saveDurationToData(duration);
            clearTimeout(this.durationFixTimeoutId);
          }}
          nodeId={nodeId}
          muted
        />
      </div>
    );
  };

  render() {
    const {
      t,
      componentData: { error },
      isLoading,
    } = this.props;
    const { isLoaded, shouldRenderDurationFix } = this.state;
    const loading = isLoading || !isLoaded;

    return (
      <div
        ref={this.containerRef}
        data-hook={VIDEO_ROOT}
        className={classNames(
          styles.video_container,
          getContainerClasses(this.props.editor, this.props.nodeId, false)
        )}
        draggable
        data-drag-handle
      >
        <div className={styles.video_overlay} />
        {this.renderPlayer()}
        {shouldRenderDurationFix ? this.renderPlayerDurationFix() : null}
        {loading && !error && this.renderLoader()}
        {error && <MediaItemErrorMsg error={error} t={t} />}
      </div>
    );
  }
}
